/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { deleteWorkspace } from "apis/workspace";
import { useToast } from "hook/useToast";
import { useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { workspace } from "recoil/recoilState";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onChange?: () => void;
  title?: string;
  content?: JSX.Element | string;
  btnTitle?: string;
};

const WorkspaceDeleteModal = (props: Props) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [workspaceInfo, setWorkspacesInfo] = useRecoilState(workspace);
  const { modalIsOpen, closeModal, onChange, title, content, btnTitle } = props;
  const [workspaceName, setWorkspaceName] = useState<string>();

  const onHandleDeleteWorkspace = async () => {
    const res = await deleteWorkspace();
    if (res.success) {
      navigate("/workspace/list", { state: { workspaceDeleteSuccess: true } });
    } else {
      showToast(res.message ?? "오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  const enabledBtn = () => {
    return workspaceInfo?.name === workspaceName;
  };

  const onChangeInput = (value: string) => {
    setWorkspaceName(value);
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelIcon>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelIcon>
        <CautionIcon>
          <UI.Icon name="inform" size={56} color="var(--alias-color-status-negative)" />
        </CautionIcon>
        <UI.Typography type="heading01" fontStyle="bold" color="var(--alias-color-label-normal)">
          워크스페이스를 삭제하시겠습니까?
        </UI.Typography>
        <UI.Typography
          type="body01 normal"
          fontStyle="medium"
          color="var(--alias-color-label-alternative)"
          style={{ marginTop: "16px", marginBottom: "16px" }}
        >
          입력창에 삭제하려는 워크스페이스명을 입력해 주세요.
          <br />
          <span
            css={css`
              color: var(--alias-color-status-negative);
            `}
          >
            *모든 데이터가 삭제되며, 복구가 불가능합니다.
          </span>
        </UI.Typography>
        <UI.Input placeholder={workspaceInfo?.name} value={workspaceName} onChange={onChangeInput} />
        <UI.Button
          type="outlined"
          title="워크스페이스 삭제하기"
          size="large"
          disabled={!enabledBtn()}
          style={{ marginTop: "40px", width: "100%" }}
          onClick={enabledBtn() ? onHandleDeleteWorkspace : () => {}}
        />
      </Container>
    </Modal>
  );
};

export default WorkspaceDeleteModal;

const Container = styled.div`
  width: 454px;
  padding: 48px;
  box-sizing: border-box;
  background-color: white;
  box-shadow: var(--alias-style-shadow-heavy);
  border-radius: 20px;
  position: relative;
  align-items: center;
  text-align: center;
`;

const CancelIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const CautionIcon = styled.div`
  box-shadow: 0px 0px 40px 0px rgba(255, 62, 62, 0.4);
  width: 47px;
  height: 47px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  margin-bottom: 24px;
`;
